import React from 'react';
import { Box, Grid, Link, styled } from '@mui/material';
import { Panel } from 'ui/Panel';
import { CardViewChipRow, CardViewTextRow, CardViewTitle } from 'ui/viewComponents';
import { contentApi } from 'components/content/contentApi';
import { ResourceText } from 'lib/ResourceText';
import { renderDeepLinkTitle } from 'components/notification/helpers';
import { RuleResource } from 'components/rules/rules.types';
import { green, grey } from '@mui/material/colors';
import { formatDateTime } from 'lib/timeHelpers';
import { getActionTypeOptions } from 'components/actions/utils';
import { flattenRuleConditions } from 'components/rules/helpers';
import { relativeTimeStringToDisplay, validateRelativeTimeString } from 'lib/relativeTimeString';
import { ActionType } from 'components/actions/models/Action';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { MARKETING_AUDIENCES_VIEW, MARKETING_VOUCHERS_VIEW } from 'config/routes';
import { voucherApi } from 'components/vouchers/voucherApi';
import { adjustmentSchemesApi } from 'components/adjustment-schemes/adjustmentSchemesApi';

type RuleAssociationLink = {
    title?: string;
    id: string;
    link?: string;
};

const PREFIX = 'RuleScheduledTaskNotificationDetailsCard';

const classes = {
    stateChip: `${PREFIX}-stateChip`
};

const StyledBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'active'
})<{ active: boolean }>(({ theme, active }) => ({
    [`& .${classes.stateChip}`]: {
        backgroundColor: active ? green['A700'] : grey['500'],
        color: theme.palette.primary.contrastText,
        fontSize: '0.8rem'
    }
}));

interface RuleScheduledTaskNotificationDetailsCardProps {
    rule: RuleResource;
}

export const RuleScheduledTaskNotificationDetailsCard: React.FC<
    RuleScheduledTaskNotificationDetailsCardProps
> = props => {
    const { rule } = props;
    const { perks } = useSelector((state: ApplicationState) => state.perks);
    const { audiences: allAudiences } = useSelector((state: ApplicationState) => state.audiences);
    const [voucher, setVoucher] = React.useState<RuleAssociationLink>(null);
    const [adjustmentScheme, setAdjustmentScheme] = React.useState<RuleAssociationLink>(null);

    const impactParameters = React.useMemo(() => {
        const impact = rule.effect && 'impact' in rule.effect ? rule.effect.impact : null;
        if (impact && impact.evaluatorName === 'passThru' && 'parameters' in impact.parameters) {
            return impact.parameters;
        }
        return null;
    }, [rule]);
    const actionType = React.useMemo<string>(() => {
        const flattenedConditions = flattenRuleConditions(rule);
        const option = getActionTypeOptions({ subscriptionsEnabled: true }).find(
            n => n.value === flattenedConditions.actionType
        );
        return `${option?.label || 'Unknown'}`;
    }, [rule]);
    const relativeTime = React.useMemo<string>(() => {
        if (!validateRelativeTimeString(impactParameters.triggerAt)) {
            return;
        }
        return relativeTimeStringToDisplay(impactParameters.triggerAt, { symbols: false });
    }, [impactParameters]);

    const audienceDetails = React.useMemo<RuleAssociationLink[]>(() => {
        const isMemberAddedOrRemoved = rule.conditions.some(
            condition =>
                condition.subject === 'type' &&
                (condition.object === ActionType.MEMBER_ADDED ||
                    condition.object === ActionType.MEMBER_REMOVED)
        );
        if (!isMemberAddedOrRemoved) {
            return [];
        }
        const flattenedConditions = flattenRuleConditions(rule);
        const audienceIds = flattenedConditions.audienceIds;
        if (!audienceIds || audienceIds.length === 0) {
            return [];
        }
        const mappedAudiences = audienceIds.map((audienceId: string) => {
            const audience = allAudiences.find(a => a._id === audienceId);
            const parsedLink = MARKETING_AUDIENCES_VIEW.replace(':audienceId', audienceId);
            return {
                title: audience?.title,
                id: audienceId,
                link: parsedLink
            };
        });
        return mappedAudiences;
    }, [rule, allAudiences]);

    const renderAudienceLink = React.useCallback(
        (audience: RuleAssociationLink, index: number) => (
            <React.Fragment key={audience.id}>
                <Link href={audience.link} target="_blank">
                    {audience.title || audience.id}
                </Link>
                {index < audienceDetails.length - 1 && <span>, </span>}
            </React.Fragment>
        ),
        [audienceDetails]
    );

    const perkDetails = React.useMemo<RuleAssociationLink | null>(() => {
        const isPerkAssociatedRule = rule.conditions.some(
            condition =>
                condition.subject === 'type' &&
                (condition.object === ActionType.REDEEM_PERK || condition.object === ActionType.REWARD_EARNED)
        );
        if (!isPerkAssociatedRule) {
            return null;
        }
        const flattenedConditions = flattenRuleConditions(rule);
        const perkId = flattenedConditions.perkId as string | undefined;
        if (!perkId) {
            return null;
        }
        const perk = perks.find(p => p._id === perkId);
        return {
            title: perk?.title,
            id: perkId
        };
    }, [rule, perks]);

    React.useEffect(() => {
        const isVoucherRedeemed = rule.conditions.some(
            condition => condition.subject === 'type' && condition.object === ActionType.VOUCHER_REDEEMED
        );
        if (!isVoucherRedeemed) {
            return;
        }
        const flattenedConditions = flattenRuleConditions(rule);
        const voucherId = flattenedConditions.voucherId;
        if (!voucherId) {
            return;
        }
        setVoucher({
            title: voucherId,
            id: voucherId
        });
        const load = async (voucherId: string) => {
            try {
                const voucherResponse = await voucherApi.get(voucherId);
                if (!voucherResponse.ok) {
                    return;
                }
                const voucher = voucherResponse.body;
                const parsedLink = MARKETING_VOUCHERS_VIEW.replace(':voucherId', voucherId);
                setVoucher({
                    title: voucher.title,
                    id: voucher._id,
                    link: parsedLink
                });
            } catch (error) {
                console.error(error);
            }
        };

        load(voucherId);
    }, [rule]);

    React.useEffect(() => {
        const isCouponRedeemed = rule.conditions.some(
            condition => condition.subject === 'type' && condition.object === ActionType.COUPON_REDEEMED
        );
        if (!isCouponRedeemed) {
            return;
        }
        const flattenedConditions = flattenRuleConditions(rule);
        const schemeId = flattenedConditions.schemeId;
        setAdjustmentScheme({
            title: schemeId,
            id: schemeId
        });
        const load = async (schemeId: string) => {
            try {
                const adjustmentSchemeResponse = await adjustmentSchemesApi.getAllSchemes();
                if (!adjustmentSchemeResponse.ok) {
                    return;
                }
                const adjustmentScheme = adjustmentSchemeResponse.items.find(
                    scheme => scheme.schemeId === schemeId
                );
                if (!adjustmentScheme) {
                    return;
                }
                setAdjustmentScheme({
                    title: adjustmentScheme.title,
                    id: schemeId
                });
            } catch (error) {
                console.error(error);
            }
        };
        load(schemeId);
    }, [rule]);

    return (
        <Panel title="Details" divider>
            <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                    <CardViewTextRow title="Send On" noTransform value={actionType} fallback="Processing" />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <CardViewTextRow
                        title="Delay By"
                        noTransform
                        value={relativeTime}
                        fallback="Processing"
                    />
                </Grid>
                {audienceDetails.length > 0 && (
                    <Grid item xs={12} lg={6}>
                        <Grid item xs={12}>
                            <CardViewTitle title="Audience" />
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
                                {audienceDetails.map(renderAudienceLink)}
                            </Box>
                        </Grid>
                    </Grid>
                )}
                {perkDetails && (
                    <Grid item xs={12} lg={6}>
                        <CardViewTextRow
                            title="Perk"
                            noTransform
                            value={perkDetails.title || perkDetails.id}
                        />
                    </Grid>
                )}
                {voucher && (
                    <Grid item xs={12} lg={6}>
                        <Grid item xs={12}>
                            <CardViewTitle title="Voucher" />
                            <Link href={voucher.link} target="_blank">
                                {voucher.title || voucher.id}
                            </Link>
                        </Grid>
                    </Grid>
                )}
                {adjustmentScheme && (
                    <Grid item xs={12} lg={6}>
                        <CardViewTextRow
                            title="Adjustment Scheme"
                            noTransform
                            value={adjustmentScheme.title || adjustmentScheme.id}
                        />
                    </Grid>
                )}
                <Grid item xs={12} lg={6}>
                    <CardViewTextRow
                        title="Created On"
                        noTransform
                        value={formatDateTime(rule?.created)}
                        fallback="Processing"
                    />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <CardViewTextRow
                        title="Updated On"
                        noTransform
                        value={formatDateTime(rule?.updated)}
                        fallback="Processing"
                    />
                </Grid>
                <Grid item xs={12}>
                    <StyledBox active={rule.active}>
                        <CardViewChipRow
                            {...props}
                            title="State"
                            value={rule.active ? 'Running' : 'Paused'}
                            chipClasses={{ root: classes.stateChip }}
                        />
                    </StyledBox>
                </Grid>
                {impactParameters.parameters?.title && (
                    <Grid item xs={12} lg={6}>
                        <CardViewTextRow title="Title" value={impactParameters.parameters.title} />
                    </Grid>
                )}
                <Grid item xs={12} lg={6}>
                    <CardViewTextRow title="Message" value={impactParameters.parameters?.message} />
                </Grid>
                {impactParameters.parameters?.imageUrl && (
                    <Grid item xs={12} lg={6}>
                        <Grid item xs={12}>
                            <CardViewTitle title="Image" />
                            <img
                                src={impactParameters.parameters.imageUrl}
                                alt="Image"
                                style={{
                                    aspectRatio: 'initial',
                                    maxWidth: '100%',
                                    maxHeight: '200px'
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
                {impactParameters.parameters?.deepLink && (
                    <Grid item xs={12} lg={6}>
                        <Grid item xs={12}>
                            <CardViewTitle title="Deep Link" noTransform />
                            <ResourceText
                                id={impactParameters.parameters.deepLink?.moduleId}
                                crud={contentApi.modules}
                                title={renderDeepLinkTitle(impactParameters.parameters.deepLink)}
                            />
                        </Grid>
                    </Grid>
                )}
                {impactParameters.parameters?.tag && (
                    <Grid item xs={12} lg={6}>
                        <CardViewTextRow title="Tag" value={impactParameters.parameters.tag} />
                    </Grid>
                )}
            </Grid>
        </Panel>
    );
};
